/** Splitting a transaction description into plain text and #hashtags. */

export interface Segment {
    text: string
    /** the tag INCLUDING its '#', or null when this segment is plain text */
    hashtag: string | null
}

// «تا جایی که به space برسه» — a tag runs from '#' to the next whitespace.
// A second '#' also ends it, so «#الف#ب» is two tags rather than one that can
// never be matched again.
const HASHTAG = /#[^\s#]+/g

// A tag written mid-sentence normally ends in punctuation: «خرید #ملزومات، و…».
// Keeping the comma inside the tag would file that transaction under a token no
// other transaction can ever share, so the punctuation is handed back to the
// plain text around it. Persian «،»/«؛»/«؟» are in here alongside their Latin
// counterparts because both are typed on Persian keyboards.
const TRAILING_PUNCTUATION = /[.,،؛;:!؟?)\]}»«"'”“…]+$/

/**
 * Split `text` into consecutive segments, hashtags marked.
 *
 * Concatenating every `text` back together reproduces the input exactly — the
 * punctuation trimmed off a tag is returned as its own plain segment, never
 * dropped.
 */
export function splitHashtags(text: string): Segment[] {
    if (!text) return []

    const segments: Segment[] = []
    let cursor = 0

    const pushPlain = (value: string) => {
        if (!value) return
        // Merge with the previous plain run so trimmed punctuation does not
        // fragment the output into a new element per character.
        const last = segments[segments.length - 1]
        if (last && last.hashtag === null) last.text += value
        else segments.push({ text: value, hashtag: null })
    }

    // `matchAll` needs ES2020 lib; this file targets the app's tsconfig, so
    // drive the sticky regex by hand instead.
    HASHTAG.lastIndex = 0
    let match = HASHTAG.exec(text)
    while (match !== null) {
        pushPlain(text.slice(cursor, match.index))

        const raw = match[0]
        const tag = raw.replace(TRAILING_PUNCTUATION, '')
        // '#' alone, or '#،' — nothing to file under.
        if (tag.length > 1) {
            segments.push({ text: tag, hashtag: tag })
            pushPlain(raw.slice(tag.length))
        } else {
            pushPlain(raw)
        }

        cursor = match.index + raw.length
        match = HASHTAG.exec(text)
    }

    pushPlain(text.slice(cursor))
    return segments
}

/** Does this description contain at least one usable hashtag? */
export function hasHashtag(text: string): boolean {
    return splitHashtags(text).some((segment) => segment.hashtag !== null)
}
